<template>
  <Onboarding>
    <div v-if="workspaceStore.workspaces.length === 0" class="my-24 w-full">
      <div class="mx-auto flex w-[420px] flex-col items-center justify-center gap-4 rounded-md bg-gray-800 p-8 shadow">
        <div class="text-surface-400 mt-2 flex flex-col items-center justify-center gap-2 text-xl">
          <Info class="text-primary mx-auto text-4xl" size="48" :strokeWidth="1" />
          <div class="flex flex-col items-center">
            <span class="text-surface-400 text-xl font-medium">No workspaces available</span>
            <span class="text-base"> Create one to get started. </span>
          </div>
        </div>
        <DButton
          label="Create Workspace"
          icon="pi pi-plus"
          @click="showCreateWorkspaceDialog = true"
          severity="primary" />
      </div>
    </div>

    <div class="mx-12 flex flex-1 flex-col md:mx-24 lg:mx-40 xl:mx-64" v-else>
      <!-- Page Header -->

      <div class="mb-8 flex gap-2">
        <DButton severity="primary">
          <div class="flex items-center gap-2 font-semibold">Projects</div>
          <span class="bg-primary-600 inline-flex h-6 w-9 items-center justify-center rounded-2xl p-0.5">{{
            assets.length
          }}</span>
        </DButton>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="flex items-center justify-center py-12">
        <div class="text-center">
          <i class="pi pi-spinner pi-spin mb-2 text-2xl text-gray-400"></i>
          <p class="text-gray-400">Loading assets...</p>
        </div>
      </div>

      <!-- Assets Error -->
      <div v-else-if="assetsError" class="py-12 text-center">
        <i class="pi pi-exclamation-triangle mb-4 text-4xl text-red-400"></i>
        <h3 class="mb-2 text-lg font-medium text-white">Error Loading Assets</h3>
        <p class="mb-4 text-red-400">{{ assetsError }}</p>
        <DButton label="Try Again" @click="onWorkspaceChange" severity="secondary" />
      </div>

      <!-- Asset List Section -->
      <div v-else-if="selectedWorkspaceId && assets.length > 0 && !assetsError" class="rounded-lg">
        <div class="grid gap-4 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-5 lg:gap-3">
          <DAsset
            type="asset"
            v-for="(asset, index) in assets"
            :key="asset.code || index"
            :resource="asset"
            shadow
            @show="viewAsset"
            @click="viewAsset(asset)"
            @contextmenu="showAssetContext($event, asset)"
            :default-menus="['']">
          </DAsset>
        </div>

        <DPaginator
          v-if="assetStore.pagination.total > assetStore.pagination.per_page"
          :rows="assetStore.pagination.per_page"
          :totalRecords="assetStore.pagination.total"
          @page="updateCurrentPage" />
      </div>

      <!-- Empty State -->
      <div
        v-else-if="!loading && selectedWorkspaceId && workspaceStore.workspaces.length > 0"
        class="mx-auto my-24 flex w-[420px] flex-col items-center justify-center rounded-lg bg-gray-800 p-8">
        <div class="text-center">
          <FolderOpen class="text-primary mx-auto mb-4 text-4xl" size="48" :strokeWidth="1" />
          <h3 class="mb-2 text-xl font-medium text-gray-400">No Assets found</h3>

          <DButton
            class="mt-4"
            label="Create New Mapping Matter Project"
            icon="pi pi-plus"
            @click="showNewProjectDialog = !showNewProjectDialog"
            severity="primary" />
        </div>
      </div>

      <!-- No Workspace Selected State -->
      <div
        v-else-if="!selectedWorkspaceId && workspaceStore.workspaces.length > 0"
        class="mx-auto my-24 flex w-[420px] flex-col items-center justify-center rounded-lg bg-gray-800 p-8">
        <div class="py-12 text-center">
          <i class="pi pi-folder mb-4 text-4xl text-gray-400"></i>

          <h3 class="mb-2 text-xl font-medium text-white">Select a workspace</h3>
          <p class="text text-gray-400">Choose a workspace from the dropdown above to browse its assets.</p>
        </div>
      </div>
    </div>
    <!-- Create Workspace Dialog -->
    <DDialog
      v-model:visible="showCreateWorkspaceDialog"
      header="Create New Workspace"
      :modal="true"
      :closable="true"
      class="w-full max-w-md"
      :draggable="false">
      <div class="space-y-4 p-4">
        <div>
          <label class="mb-2 block text-sm font-medium text-gray-300"> Workspace Name </label>
          <DInputText
            v-model="newWorkspaceName"
            placeholder="Enter workspace name"
            class="w-full"
            :disabled="creatingWorkspace"
            @keyup.enter="createWorkspace" />
        </div>

        <div v-if="createWorkspaceError" class="rounded-lg border border-red-700 bg-red-900/20 p-3">
          <div class="text-sm text-red-400">
            <i class="pi pi-exclamation-triangle mr-1"></i>
            {{ createWorkspaceError }}
          </div>
        </div>
      </div>

      <template #footer>
        <div class="flex justify-end gap-2">
          <DButton label="Cancel" @click="cancelCreateWorkspace" :disabled="creatingWorkspace" />
          <DButton
            label="Create"
            icon="pi pi-plus"
            @click="createWorkspace"
            severity="primary"
            :loading="creatingWorkspace"
            :disabled="!newWorkspaceName.trim()" />
        </div>
      </template>
    </DDialog>

    <NewProjectDialog v-if="showNewProjectDialog" @close="showNewProjectDialog = !showNewProjectDialog" />
    <DContextMenu2 ref="contextMenuRef" :model="contextMenuItems" />
  </Onboarding>
</template>

<script setup>
import Onboarding from '@/components/layout/Onboarding.vue';
import NewProjectDialog from '@/components/NewProjectDialog.vue';
import { useAssetStore } from '@/stores/api/asset';
import { useWorkspaceStore } from '@/stores/api/workspace';
import { useAppStore } from '@/stores/appStore';
import { extractApiMessage } from '@/utils/apiResponse';
import {
  DAsset,
  DButton,
  DContextMenu2,
  DDialog,
  DInputText,
  DPaginator,
  useConfirm,
  useToast,
} from '@disguise/cloud-ui';
import { FolderOpen, Info } from 'lucide-vue-next';
import { computed, ref, watchEffect } from 'vue';
import { useRouter } from 'vue-router';

// Store instances
const workspaceStore = useWorkspaceStore();
const assetStore = useAssetStore();

const appStore = useAppStore();
const showNewProjectDialog = ref(false);
// Reactive data
const selectedWorkspaceId = ref(localStorage.getItem('crux-selected-workspace-id') || null);
const assetsError = ref(null);
const showCreateWorkspaceDialog = ref(false);
const newWorkspaceName = ref('');
const creatingWorkspace = ref(false);
const createWorkspaceError = ref(null);
const router = useRouter();
const assets = computed(() => assetStore.assets || []);

const { confirmDialog } = useConfirm();
const toast = useToast();
const contextMenuRef = ref(null);
const selectedAssetForMenu = ref(null);

const showAssetContext = ($event, asset) => {
  selectedAssetForMenu.value = asset;
  contextMenuRef.value.show($event);
};

const loading = ref(false);

const contextMenuItems = ref([
  {
    label: 'Delete',
    icon: 'pi pi-trash',
    command: () => {
      const asset = selectedAssetForMenu.value;
      if (!asset) return;
      confirmDialog(
        'Confirmation',
        `Are you sure you want to delete "${asset.name || 'this asset'}"?`,
        'Delete',
        'Cancel'
      ).then(async (response) => {
        if (response) {
          try {
            await assetStore.destroy(asset.code);
            toast.add({
              severity: 'success',
              summary: 'Asset Deleted',
              detail: `${asset.name || 'Asset'} has been deleted`,
              group: 'br',
              life: 3000,
            });
            await assetStore.fetchForWorkspace(selectedWorkspaceId.value, {
              asset_type: 'CX',
              page: assetStore.pagination.page || 1,
              sort_by: assetStore.pagination.sort_by,
            });
          } catch (error) {
            console.error('Error deleting asset:', error);
            toast.add({
              severity: 'error',
              summary: 'Delete Failed',
              detail: `Failed to delete ${asset.name || 'asset'}`,
              group: 'br',
              life: 5000,
            });
          }
        }
      });
    },
  },
]);

const onWorkspaceChange = async (page = 1) => {
  if (!selectedWorkspaceId.value) return;
  appStore.setLoading(true);
  appStore.setDisplayLoadingToast(true);
  assetsError.value = null;
  try {
    await assetStore.fetchForWorkspace(selectedWorkspaceId.value, {
      asset_type: 'CX',
      page,
      sort_by: assetStore.pagination.sort_by,
    });
  } catch (error) {
    console.error('Error loading assets:', error);
    assetsError.value = 'Failed to load assets. Please try again.';
  } finally {
    loading.value = false;
    appStore.setLoading(false);
    appStore.setDisplayLoadingToast(false);
  }
};

const viewAsset = (asset) => {
  router.push(`/editor/${asset.latest_version.asset}`);
};

const createWorkspace = async () => {
  if (!newWorkspaceName.value.trim()) return;

  creatingWorkspace.value = true;
  createWorkspaceError.value = null;
  try {
    const response = await workspaceStore.store({ name: newWorkspaceName.value.trim() });
    const { error } = response || {};

    if (error) {
      createWorkspaceError.value = extractApiMessage(error, 'Failed to create workspace. Please try again.');
      return;
    }

    // Reload workspaces to get the newly created one
    await workspaceStore.fetch();

    // Select the newly created workspace
    if (workspaceStore.workspaces.length > 0) {
      const newWorkspace = workspaceStore.workspaces.find((w) => w.name === newWorkspaceName.value.trim());

      if (newWorkspace) {
        selectedWorkspaceId.value = newWorkspace.code;
        workspaceStore.currentWorkspace = selectedWorkspaceId.value;
      }
    }
  } catch (error) {
    console.error('Error creating workspace:', error);
    createWorkspaceError.value = extractApiMessage(error, 'Failed to create workspace. Please try again.');
    return;
  } finally {
    creatingWorkspace.value = false;
  }

  // Close dialog and reset form
  showCreateWorkspaceDialog.value = false;
  newWorkspaceName.value = '';
};

const cancelCreateWorkspace = () => {
  showCreateWorkspaceDialog.value = false;
  newWorkspaceName.value = '';
  createWorkspaceError.value = null;
};

const updateCurrentPage = async (event) => {
  await onWorkspaceChange(event.page + 1);
};

watchEffect(() => {
  selectedWorkspaceId.value = workspaceStore.currentWorkspace ?? null;
});
</script>
